const API_BASE = process.env.REACT_APP_API_URL || 'https://backend-951385075735.europe-west1.run.app';

class ApiClient {
    getToken() {
        return localStorage.getItem('token');
    }

    setToken(token) {
        localStorage.setItem('token', token);
    }

    clearToken() {
        localStorage.removeItem('token');
    }

    async request(path, options = {}) {
        const headers = { ...options.headers };
        const token = this.getToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        const res = await fetch(`${API_BASE}${path}`, {
            ...options,
            headers,
        });

        if (res.status === 401) {
            this.clearToken();
            window.dispatchEvent(new Event('auth:logout'));
        }

        const data = await res.json().catch(() => null);
        if (!res.ok) {
            const message = data?.message || `Request failed (${res.status})`;
            throw new Error(message);
        }
        return data;
    }

    get(path) {
        return this.request(path);
    }

    post(path, body) {
        return this.request(path, {
            method: 'POST',
            body: body instanceof FormData ? body : JSON.stringify(body),
        });
    }

    patch(path, body) {
        return this.request(path, {
            method: 'PATCH',
            body: JSON.stringify(body),
        });
    }

    delete(path) {
        return this.request(path, { method: 'DELETE' });
    }

    // Auth
    register(email, password, username) {
        return this.post('/auth/register', { email, password, username });
    }

    confirmRegistration(token) {
        return this.post('/auth/register-confirm', { token });
    }

    login(emailOrUsername, password) {
        return this.post('/auth/login', { emailOrUsername, password });
    }

    // Exchanges a Firebase Google ID token for an ArtSyde session token.
    async googleLogin(firebaseIdToken) {
        const res = await fetch(`${API_BASE}/auth/google`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${firebaseIdToken}` },
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) {
            throw new Error(data?.message || `Google sign-in failed (${res.status})`);
        }
        return data;
    }

    logout() {
        return this.post('/auth/logout');
    }

    forgotPassword(email) {
        return this.post('/auth/forgot-password', { email });
    }

    resetPassword(token, newPassword) {
        return this.post('/auth/reset-password', { token, newPassword });
    }

    getSessions() {
        return this.get('/auth/sessions');
    }

    deleteSession(sessionId) {
        return this.delete(`/auth/sessions/${sessionId}`);
    }

    deleteAllSessions() {
        return this.delete('/auth/sessions');
    }

    requestAccountDeletion() {
        return this.post('/auth/request-account-deletion');
    }

    confirmAccountDeletion(token) {
        return this.post('/auth/confirm-account-deletion', { token });
    }

    // User
    getProfile() {
        return this.get('/user/profile');
    }

    updateProfile(data) {
        return this.patch('/user/me', data);
    }

    changePassword(currentPassword, newPassword) {
        return this.patch('/user/my_password', { currentPassword, newPassword });
    }

    getFollowers(skip = 0, limit = 25) {
        return this.get(`/user/followers?skip=${skip}&limit=${limit}`);
    }

    getFollowing(skip = 0, limit = 25) {
        return this.get(`/user/following?skip=${skip}&limit=${limit}`);
    }

    getUserFollowers(publicId, skip = 0, limit = 25) {
        return this.get(`/user/${publicId}/followers?skip=${skip}&limit=${limit}`);
    }

    getUserFollowing(publicId, skip = 0, limit = 25) {
        return this.get(`/user/${publicId}/following?skip=${skip}&limit=${limit}`);
    }

    uploadProfilePicture(file) {
        const form = new FormData();
        form.append('file', file);
        return this.post('/user/me/profile-picture', form);
    }

    uploadBannerPicture(file) {
        const form = new FormData();
        form.append('file', file);
        return this.post('/user/me/banner-picture', form);
    }

    findUser(publicId) {
        return this.get(`/user/find?publicId=${encodeURIComponent(publicId)}`);
    }

    searchUsers(username) {
        return this.get(`/user/finds?username=${encodeURIComponent(username)}`);
    }

    searchUsersPaginated(username, skip = 0, limit = 25) {
        return this.get(`/user/search?username=${encodeURIComponent(username || '')}&skip=${skip}&limit=${limit}`);
    }

    subscribe(publicUserId) {
        return this.post(`/user/subscribe/${publicUserId}`);
    }

    unsubscribe(publicUserId) {
        return this.post(`/user/unsubscribe/${publicUserId}`);
    }

    // Pictures
    getHomeFeed(skip = 0, limit = 20) {
        return this.get(`/picture?skip=${skip}&limit=${limit}`);
    }

    getFeed(options = {}) {
        const params = new URLSearchParams();
        if (options.category) params.set('category', options.category);
        if (options.sortByLikes) params.set('sortByLikes', 'true');
        if (options.skip != null) params.set('skip', options.skip);
        if (options.limit != null) params.set('limit', options.limit);
        return this.get(`/picture/feed?${params}`);
    }

    getOwnPictures(skip = 0, limit = 20) {
        return this.get(`/picture/own?skip=${skip}&limit=${limit}`);
    }

    getPicture(publicId) {
        return this.get(`/picture/${publicId}`);
    }

    uploadPicture(file, { title, description, price, category }) {
        const form = new FormData();
        form.append('file', file);
        if (title) form.append('title', title);
        form.append('description', description || '');
        if (price != null) form.append('price', String(price));
        form.append('category', category);
        return this.post('/picture/upload', form);
    }

    updatePicture(id, data) {
        return this.patch(`/picture/${id}`, data);
    }

    deletePicture(publicId) {
        return this.delete(`/picture/${publicId}`);
    }

    likePicture(publicId) {
        return this.post(`/picture/${publicId}/like`);
    }

    getFollowingUploads(skip = 0, limit = 20) {
        return this.get(`/picture/following/latest-uploads?skip=${skip}&limit=${limit}`);
    }

    getLikedPictures(skip = 0, limit = 20) {
        return this.get(`/picture/get-liked-pictures?skip=${skip}&limit=${limit}`);
    }

    getUserPictures(publicUserId, skip = 0, limit = 20) {
        return this.get(`/picture/get-user-pictures/${publicUserId}?skip=${skip}&limit=${limit}`);
    }

    searchPictures(q, category, skip = 0, limit = 20) {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        if (category) params.set('category', category);
        params.set('skip', skip);
        params.set('limit', limit);
        return this.get(`/picture/search?${params}`);
    }

    // Comments
    getComments(picturePublicId, skip = 0, limit = 20) {
        return this.get(`/picture/${picturePublicId}/comments?skip=${skip}&limit=${limit}`);
    }

    getReplies(picturePublicId, commentPublicId, skip = 0, limit = 20) {
        return this.get(`/picture/${picturePublicId}/comments/${commentPublicId}/replies?skip=${skip}&limit=${limit}`);
    }

    addComment(picturePublicId, text, parentPublicId) {
        const body = { text };
        if (parentPublicId) body.parentPublicId = parentPublicId;
        return this.post(`/picture/${picturePublicId}/comments`, body);
    }

    likeComment(commentPublicId) {
        return this.post(`/comments/${commentPublicId}/like`);
    }

    editComment(commentPublicId, text) {
        return this.patch(`/comments/${commentPublicId}`, { text });
    }

    deleteComment(commentPublicId) {
        return this.delete(`/comments/${commentPublicId}`);
    }

    // Basket
    getBasket() {
        return this.get('/basket');
    }

    getBasketSize() {
        return this.get('/basket/size');
    }

    addToBasket(publicId) {
        return this.patch('/basket', { publicId, action: 'add' });
    }

    removeFromBasket(publicId) {
        return this.patch('/basket', { publicId, action: 'remove' });
    }

    // Discussions
    createDiscussion(participantIds, name) {
        const body = { participantIds };
        if (name) body.name = name;
        return this.post('/discussions', body);
    }

    getDiscussions(skip = 0, limit = 20) {
        return this.get(`/discussions?skip=${skip}&limit=${limit}`);
    }

    getDiscussion(discussionId) {
        return this.get(`/discussions/${discussionId}`);
    }

    updateDiscussion(discussionId, data) {
        return this.patch(`/discussions/${discussionId}`, data);
    }

    getMessages(discussionId, skip = 0, limit = 30) {
        return this.get(`/discussions/${discussionId}/messages?skip=${skip}&limit=${limit}`);
    }

    sendMessage(discussionId, data) {
        return this.post(`/discussions/${discussionId}/messages`, data);
    }

    editMessage(discussionId, messageId, content) {
        return this.patch(`/discussions/${discussionId}/messages/${messageId}`, { content });
    }

    deleteMessage(discussionId, messageId) {
        return this.delete(`/discussions/${discussionId}/messages/${messageId}`);
    }

    getDiscussionsUnreadCount() {
        return this.get('/discussions/unread-count');
    }

    getDiscussionUnreadCount(discussionId) {
        return this.get(`/discussions/${discussionId}/unread-count`);
    }

    markDiscussionRead(discussionId) {
        return this.post(`/discussions/${discussionId}/read`);
    }

    // Identity verification (user)
    getMyVerification() {
        return this.get('/user/verification');
    }

    uploadVerificationDoc(slot, file) {
        // slot: 'id-front' | 'id-back' | 'artist-document'
        const form = new FormData();
        form.append('file', file);
        return this.post(`/user/verification/${slot}`, form);
    }

    submitVerification() {
        return this.post('/user/verification/submit');
    }

    // Identity verification (admin)
    adminListVerifications(status, skip = 0, limit = 25) {
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        params.set('skip', skip);
        params.set('limit', limit);
        return this.get(`/admin/verifications?${params}`);
    }

    adminGetVerification(id) {
        return this.get(`/admin/verifications/${id}`);
    }

    approveVerification(id) {
        return this.post(`/admin/verifications/${id}/approve`);
    }

    rejectVerification(id, reason) {
        return this.post(`/admin/verifications/${id}/reject`, { reason });
    }

    // Notifications
    getNotifications(skip = 0, limit = 25) {
        return this.get(`/notifications?skip=${skip}&limit=${limit}`);
    }

    getUnreadNotificationCount() {
        return this.get('/notifications/unread-count');
    }

    getPictureLikers(picturePublicId, skip = 0, limit = 25) {
        return this.get(`/notifications/picture/${picturePublicId}/likes?skip=${skip}&limit=${limit}`);
    }

    getCommentLikers(commentPublicId, skip = 0, limit = 25) {
        return this.get(`/notifications/comment/${commentPublicId}/likes?skip=${skip}&limit=${limit}`);
    }

    markAllNotificationsRead() {
        return this.patch('/notifications/read-all', {});
    }

    markNotificationRead(publicId) {
        return this.patch(`/notifications/${publicId}/read`, {});
    }

    // Support (registered user)
    getSupportMessages() {
        return this.get('/support/messages');
    }

    sendSupportMessage(content, platform = 'web', attachments) {
        const body = { content, platform };
        if (attachments && attachments.length) body.attachments = attachments;
        return this.post('/support/messages', body);
    }

    getSupportTicket() {
        return this.get('/support/ticket');
    }

    getSupportUnreadCount() {
        return this.get('/support/unread-count');
    }

    // Support (admin)
    getAllTickets(status, page = 1, limit = 20) {
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        params.set('page', page);
        params.set('limit', limit);
        return this.get(`/support/tickets?${params}`);
    }

    searchTickets(q, page = 1, limit = 20) {
        return this.get(`/support/tickets/search?q=${encodeURIComponent(q || '')}&page=${page}&limit=${limit}`);
    }

    getTicketAdmin(ticketId) {
        return this.get(`/support/tickets/${ticketId}`);
    }

    sendAdminReply(ticketId, content, attachments) {
        const body = { content };
        if (attachments && attachments.length) body.attachments = attachments;
        return this.post(`/support/tickets/${ticketId}/messages`, body);
    }

    updateTicket(ticketId, data) {
        return this.request(`/support/tickets/${ticketId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    closeTicket(ticketId) {
        return this.post(`/support/tickets/${ticketId}/close`);
    }

    deleteTicket(ticketId) {
        return this.delete(`/support/tickets/${ticketId}`);
    }

    // Admin
    getErrorLogs(options = {}) {
        const params = new URLSearchParams();
        if (options.skip != null) params.set('skip', options.skip);
        if (options.limit != null) params.set('limit', options.limit);
        if (options.level) params.set('level', options.level);
        if (options.service) params.set('service', options.service);
        return this.get(`/admin/errors?${params}`);
    }

    getErrorStats() {
        return this.get('/admin/errors/stats');
    }

    getErrorDetail(id) {
        return this.get(`/admin/errors/${id}`);
    }

    cleanupErrors(days = 30) {
        return this.delete(`/admin/errors/cleanup?days=${days}`);
    }

    // Localization (static assets served from the web root, not the API base)
    async getTranslations(lang) {
        const res = await fetch(`/lang/${lang}.json`);
        if (!res.ok) {
            throw new Error(`Failed to load translations (${res.status})`);
        }
        return res.json();
    }
}

const api = new ApiClient();
export default api;
